import { yupResolver } from '@hookform/resolvers/yup';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import {
  Control,
  SubmitErrorHandler,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFormGetValues,
  UseFormSetValue,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TossMaterial, TossMaterialCategoryTree, TossMaterialFile, TossMaterialGroup } from '../../../../../api/model';
import yup from '../../../../../utils/custom-yup';
import DatePicker from '../../../../atoms/DatePicker';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import SlideToggle from '../../../../modules/SlideToggle';
import { DroppableTd, InnerTabCommonNavi } from './CommonComponents';

interface FListTrProps {
  productionId: number; // 制作管理 ID
  i: number; // 配列のインデックス 1
  j: number; // 配列のインデックス 2
  tossMaterial: TossMaterial; // 表示対象のトス資料データ
  editable: boolean; // 編集モードかどうか
  setValue: UseFormSetValue<{
    toss_materials: TossMaterial[][];
  }>; // useForm で返ってくる setValue
  getValues: UseFormGetValues<{
    toss_materials: TossMaterial[][];
  }>; // useForm で返ってくる getValues
  control: Control<{
    toss_materials: TossMaterial[][];
  }>; // useForm で返ってくる control
  replace: UseFieldArrayReplace<
    {
      toss_materials: TossMaterial[][];
    },
    `toss_materials.${number}`
  >; // useFieldArray で返ってくる replace
  remove: UseFieldArrayRemove; // useFieldArray で返ってくる remove
}

/**
 * FList タブの各行
 * @param param0
 * @returns
 */
function FListTr({
  productionId,
  i,
  j,
  tossMaterial,
  editable,
  setValue,
  getValues,
  control,
  replace,
  remove,
}: FListTrProps) {
  // コメントの値をリアルタイムに取得
  const comment = useWatch({ control, name: `toss_materials.${i}.${j}.comment` });

  // file1 の値をリアルタイムに取得
  const file1 = useWatch({ control, name: `toss_materials.${i}.${j}.file1` });

  // file2 の値をリアルタイムに取得
  const file2 = useWatch({ control, name: `toss_materials.${i}.${j}.file2` });

  // ファイルドロップが成功した場合の処理
  const onDropSucceeded = useCallback(
    (files: TossMaterialFile[], setted: boolean, name: 'file1' | 'file2') => {
      // 現在表示されているトス資料の情報
      const prevTossMaterials = getValues().toss_materials[i];

      // 次に設定するトス資料情報
      const nextTossMaterials: TossMaterial[] = [];

      if (setted) {
        // もともとその行にファイルが設定してあった場合

        // そのまま表示するデータ
        for (let k = 0; k <= j; k += 1) {
          nextTossMaterials.push(prevTossMaterials[k]);
        }

        // アップロードされたファイルを順番に表示する
        files.forEach((w) => {
          const newTossMaterial: TossMaterial = {
            ...prevTossMaterials[j],
          };
          newTossMaterial[name] = w;

          // ファイルはコピーしない
          newTossMaterial[name === 'file1' ? 'file2' : 'file1'] = undefined;

          // ファイルがアップロードされた行以外は ID、最終更新日、更新者をリセット
          newTossMaterial.toss_material_id = undefined;
          newTossMaterial.updated_on = undefined;
          newTossMaterial.update_user_name = undefined;

          nextTossMaterials.push(newTossMaterial);
        });

        // 下にずらして表示するデータ
        for (let k = j + 1; k < prevTossMaterials.length; k += 1) {
          nextTossMaterials.push(prevTossMaterials[k]);
        }
      } else {
        // もともとその行にはファイルが設定していなかった場合

        // そのまま表示するデータ
        for (let k = 0; k < j; k += 1) {
          nextTossMaterials.push(prevTossMaterials[k]);
        }

        // アップロードされたファイルを順番に表示する
        files.forEach((w, k) => {
          const newTossMaterial: TossMaterial = {
            ...prevTossMaterials[j],
          };
          newTossMaterial[name] = w;
          if (k !== 0) {
            // ファイルはコピーしない
            newTossMaterial[name === 'file1' ? 'file2' : 'file1'] = undefined;

            // ファイルがアップロードされた行以外は ID、最終更新日、更新者をリセット
            newTossMaterial.toss_material_id = undefined;
            newTossMaterial.updated_on = undefined;
            newTossMaterial.update_user_name = undefined;
          }
          nextTossMaterials.push(newTossMaterial);
        });

        // 下にずらして表示するデータ
        for (let k = j + 1; k < prevTossMaterials.length; k += 1) {
          nextTossMaterials.push(prevTossMaterials[k]);
        }
      }
      replace(nextTossMaterials);
    },
    [getValues, i, j, replace]
  );

  // ファイル名を変更する場合の処理
  const onChangeMaterialFileName = useCallback(
    (next: string | undefined, name: 'file1' | 'file2') =>
      setValue(`toss_materials.${i}.${j}.${name}.file_name`, next ?? ''),
    [i, j, setValue]
  );

  // ファイル削除する場合の処理
  const onClickDelete = useCallback(
    (name: 'file1' | 'file2') => {
      // ファイルを空に
      setValue(`toss_materials.${i}.${j}.${name}`, undefined);
    },
    [setValue, i, j]
  );

  // 行削除する場合の処理
  const onClickDeleteRow = useCallback(() => remove(j), [remove, j]);

  // タブ内のトス資料名を全て取得する
  const getPresentFilenames = useCallback(() => {
    const values = getValues();
    const filenames = values.toss_materials
      .flat()
      .flat()
      .flatMap((x) => {
        if (x.file1?.file_name != null && x.file2?.file_name != null) {
          return [x.file1?.file_name, x.file2?.file_name];
        }
        if (x.file1?.file_name != null) {
          return x.file1?.file_name;
        }
        if (x.file2?.file_name != null) {
          return x.file2?.file_name;
        }
        return [];
      });
    return filenames;
  }, [getValues]);

  return (
    <tr>
      <td>{tossMaterial.rev === 0 ? 'その他' : tossMaterial.rev}</td>
      {/* コメント */}
      <td>
        {/* 5文字ごとに改行を入れる */}
        {(comment ?? '').split('').map((z, k) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={k}>
            {k > 0 && k % 20 === 0 ? <br /> : null}
            {z}
          </React.Fragment>
        ))}
      </td>
      <td>
        <DatePicker name={`toss_materials.${i}.${j}.issued_at`} isDisabled={!editable} control={control} displayMark />
      </td>
      {/* トス資料ファイル1 */}
      <DroppableTd
        productionId={productionId}
        tabIndex={3 as number}
        editable={editable}
        file={file1}
        comment={comment}
        getPresentFilenames={getPresentFilenames}
        onDropSucceeded={(files) => onDropSucceeded(files, file1 != null, 'file1')}
        onChangeComment={(next) => {
          setValue(`toss_materials.${i}.${j}.comment`, next);
        }}
        onChangeMaterialFileName={(next) => onChangeMaterialFileName(next, 'file1')}
        onClickDelete={() => onClickDelete('file1')}
        onCickDeleteRow={
          tossMaterial.is_initial === true || file1 != null || file2 != null ? undefined : onClickDeleteRow
        }
      />
      {/* トス資料ファイル2 */}
      {[1, 2].includes(i) && (
        <DroppableTd
          productionId={productionId}
          tabIndex={3 as number}
          editable={editable}
          file={file2}
          comment={undefined}
          getPresentFilenames={getPresentFilenames}
          onDropSucceeded={(files) => onDropSucceeded(files, file2 != null, 'file2')}
          onChangeComment={undefined}
          onChangeMaterialFileName={(next) => onChangeMaterialFileName(next, 'file2')}
          onClickDelete={() => onClickDelete('file2')}
          onCickDeleteRow={
            tossMaterial.is_initial === true || file1 != null || file2 != null ? undefined : onClickDeleteRow
          }
        />
      )}
      <td>{tossMaterial.updated_on?.replaceAll('-', '/')}</td>
      <td>{tossMaterial.update_user_name}</td>
    </tr>
  );
}

interface FListTableProps {
  productionId: number; // 制作管理 ID
  productionCategoryId: number; // 制作管理カテゴリ ID
  tossMaterialGroup: TossMaterialGroup; // 表示対象のトス資料グループ
  i: number; // 配列のインデックス 1
  editable: boolean; // 編集モードかどうか
  open: boolean; // SlideToggle 開くかどうか
  toggleOpen: () => void; // SlideToggle の開閉
  setValue: UseFormSetValue<{
    toss_materials: TossMaterial[][];
  }>; // useForm で返ってくる setValue
  getValues: UseFormGetValues<{
    toss_materials: TossMaterial[][];
  }>; // useForm で返ってくる getValues
  control: Control<{
    toss_materials: TossMaterial[][];
  }>; // useForm で返ってくる control
}

/**
 * FList タブの個別の表
 * @param param0
 * @returns
 */
function FListTable({
  i,
  productionId,
  productionCategoryId,
  editable,
  tossMaterialGroup,
  open,
  toggleOpen,
  setValue,
  getValues,
  control,
}: FListTableProps) {
  // Form が表になるときに利用する
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `toss_materials.${i}`,
  });

  // 言語
  const { t } = useTranslation();

  // 二列目のヘッダー
  const column2Header = useMemo(() => {
    let result = t('資料');
    if (i === 1) {
      result = t('FRM');
    } else if (i === 2) {
      result = t('その他');
    }
    return result;
  }, [i, t]);

  // 三列目のヘッダー
  const column3Header = useMemo(() => {
    let result: string | undefined;
    if (i === 1) {
      result = t('ENG/MIS').toString();
    } else if (i === 2) {
      result = t('その他').toString();
    }
    return result;
  }, [i, t]);

  if (tossMaterialGroup.toss_material_group_id == null || productionCategoryId == null) {
    return null;
  }

  return (
    <>
      {/* トス資料のグループ名 */}
      <h3 className="mdh2">
        <span>
          {tossMaterialGroup.group_name}
          {/* 表示しているトス資料の件数 */}
          <button
            type="button"
            className="text-button"
            onClick={() => {
              // ▽や△を押すことで、詳細部分を広げたり閉じたりする
              toggleOpen();
            }}
          >
            <i className={`fa-solid fa-chevron-${open ? 'down' : 'up'}`} />
          </button>
        </span>
      </h3>
      {/* 開閉される部分 */}
      <SlideToggle open={open}>
        <div id="master31" className="masterfr2">
          {/* 新規追加ボタン */}
          <p className="newadd">
            <button
              type="button"
              className="text-button"
              onClick={() => {
                if (!editable) return;

                // 現在の REV の最大値を取得
                const maxRev = fields.length > 0 ? Math.max(...fields.map((y) => y.rev ?? 0)) : 0;

                // 新規追加した行の初期値を設定する
                const newTossMaterial: TossMaterial = {
                  toss_material_id: undefined,
                  toss_material_production_category_id: productionCategoryId,
                  toss_material_group_id: tossMaterialGroup.toss_material_group_id,
                  toss_material_subgroup_id: undefined,
                  toss_material_sending_material_id: undefined,
                  toss_material_material_name_id: undefined,
                  check: false,
                  toss_material_target_manual_id: undefined,
                  sp: undefined,
                  rev: i === 2 ? 0 : maxRev + 1, // 「その他」の REV は 「その他」を意味する 0
                  toss_material_schedule_id: undefined,
                  comment: undefined,
                  is_initial: false,
                  sort_order: 0,
                  file1: undefined,
                  file2: undefined,
                  share_destination_productions: undefined,
                  update_user_name: undefined,
                  updated_on: undefined,
                  issued_at: undefined,
                };
                append(newTossMaterial);
              }}
              disabled={!editable}
            >
              <i className="fa-solid fa-circle-plus" />
              {t('新規追加')}
            </button>
          </p>
          {/* 表本体 */}
          <table className="table50">
            <tbody>
              <tr>
                <th>{t('REV')}</th>
                <th>{t('コメント')}</th>
                <th className="data">{t('date')}</th>
                <th>{column2Header}</th>
                {column3Header != null && <th>{column3Header}</th>}
                <th>{t('最終更新日')}</th>
                <th>{t('更新者')}</th>
              </tr>
              {fields.map((y, j) => (
                <FListTr
                  key={y.id}
                  productionId={productionId}
                  i={i}
                  j={j}
                  tossMaterial={y}
                  editable={editable}
                  setValue={setValue}
                  getValues={getValues}
                  control={control}
                  replace={replace}
                  remove={remove}
                />
              ))}
            </tbody>
          </table>
        </div>
      </SlideToggle>
    </>
  );
}

/**
 * トス資料ツリーからフォームの初期値を取得する
 * @param tree
 * @returns
 */
const getDefaultValues = (tree: TossMaterialCategoryTree): { toss_materials: TossMaterial[][] } => {
  const result: TossMaterial[][] = [];
  (tree.toss_material_groups ?? []).forEach((x) => {
    result.push(x.toss_materials ?? []);
  });
  return { toss_materials: result };
};

interface FListTabProps {
  productionId: number; // 制作管理 ID
  tree: TossMaterialCategoryTree; // 表示対象のトス資料ツリー
  editable: boolean; // 編集状態かどうか
  save: (tossMaterialList: TossMaterial[]) => Promise<void>; // 保存ボタン押下時の動作
  edit: () => Promise<void>; // 編集ボタン押下時の動作
  cancel: () => Promise<void>; // キャンセル実行時の動作
  setDisplayValidationError: (next: boolean) => void; // バリデーションエラーを表示する
}

export interface FListTabHandler {
  validate: () => Promise<boolean>; // 外部からバリデーションする
  submit: () => Promise<void>; // フォームを送信する
  refresh: () => void; // フォームをリフレッシュする
}

/**
 * FList タブ
 */
const FListTab = forwardRef<FListTabHandler, FListTabProps>(
  ({ productionId, tree, editable, save, edit, cancel, setDisplayValidationError }, ref) => {
    // バリデーション用のスキーマ
    const schema = yup.object().shape({
      toss_materials: yup
        .array()
        .required()
        .of(
          yup
            .array()
            .required()
            .of(
              yup.object().shape({
                toss_material_id: yup
                  .number()
                  .nullable()
                  .transform((v) => (Number.isNaN(Number(v)) ? null : Number(v)))
                  .default(null),
                toss_material_production_category_id: yup.number().required(),
                toss_material_group_id: yup.number().required(),
                toss_material_subgroup_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                toss_material_sending_material_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                toss_material_material_name_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                check: yup
                  .boolean()
                  .required()
                  .transform(() => false),
                toss_material_target_manual_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                sp: yup
                  .string()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                rev: yup.number().nullable().required(),
                toss_material_schedule_id: yup
                  .number()
                  .nullable()
                  .transform(() => null)
                  .default(null),
                comment: yup.string().nullable().default(null),
                is_initial: yup.boolean().required(),
                sort_order: yup.number().required(),
                file1: yup
                  .object()
                  .nullable()
                  .shape({
                    toss_material_file_id: yup
                      .number()
                      .nullable()
                      .transform((v) => (v || v === 0 ? Number(v) : null)),
                    file_name: yup
                      .string()
                      .nullable()
                      .transform((v) => (v === '' ? null : String(v))),
                  })
                  .default(null),
                file2: yup
                  .object()
                  .nullable()
                  .shape({
                    toss_material_file_id: yup
                      .number()
                      .nullable()
                      .transform((v) => (v || v === 0 ? Number(v) : null)),
                    file_name: yup
                      .string()
                      .nullable()
                      .transform((v) => (v === '' ? null : String(v))),
                  })
                  .default(null),
                share_destination_productions: yup.array().nullable().default(null),
                update_user_name: yup.string().nullable().default(null),
                updated_on: yup.string().date().nullable().default(null),
                issued_at: yup.string().date().nullable().default(null),
              })
            )
        ),
    });

    // フォームを利用できるようにする
    const { handleSubmit, setValue, getValues, control, reset } = useForm({
      defaultValues: getDefaultValues(tree),
      resolver: yupResolver(schema),
    });

    // 詳細部分が開いているグループの ID
    const [openGroupIds, setOpenGroupIds] = useState<Array<number | undefined>>([]);

    // バリデーション
    const validate = useCallback(async () => {
      let result = false as boolean;
      await handleSubmit(() => {
        result = true;
      })();
      return result;
    }, [handleSubmit]);

    // 最新データで表示し直し
    const refresh = useCallback(() => {
      reset(getDefaultValues(tree));
    }, [reset, tree]);

    // 保存ボタン押下時の動作
    const submit = useCallback(async () => {
      // バリデーション結果が有効だった場合
      const onValid = async (data: { toss_materials: TossMaterial[][] }) => {
        // API 実行
        const tossMaterialList = data.toss_materials.flat();

        await save(tossMaterialList);
      };

      // バリデーション結果が無効だった場合（エラーモーダルを表示）
      const onInvalid: SubmitErrorHandler<{ toss_materials: TossMaterial[][] }> = () => {
        // バリデーションエラーをモーダルで表示
        setDisplayValidationError(true);
      };

      // フォーム送信時の処理を設定
      await handleSubmit(onValid, onInvalid)();
    }, [handleSubmit, save, setDisplayValidationError]);

    // 親コンポーネントから操作できるようにする
    useImperativeHandle(ref, () => ({
      validate,
      submit,
      refresh,
    }));

    // 必要なデータがない場合
    if (tree.toss_material_groups == null) {
      return (
        <div id="tab2-content3" className="tab-content2">
          <NoDataComponent />
        </div>
      );
    }

    return (
      <div id="tab2-content3" className="tab-content2">
        <form>
          {/* 共通ナビ */}
          <InnerTabCommonNavi
            productionId={productionId}
            tabIndex={3 as number}
            editable={editable}
            open={() => setOpenGroupIds((tree.toss_material_groups ?? []).map((x) => x.toss_material_group_id))}
            close={() => setOpenGroupIds([])}
            edit={edit}
            cancel={cancel}
          />
          {/* 本体 トス資料グループごとに表を描画 */}
          {tree.toss_material_groups.map((x, i) => {
            if (tree.toss_material_production_category_id == null) {
              return <React.Fragment key={x.toss_material_group_id} />;
            }

            return (
              <FListTable
                key={x.toss_material_group_id}
                tossMaterialGroup={x}
                open={openGroupIds.includes(x.toss_material_group_id)}
                productionId={productionId}
                i={i}
                productionCategoryId={tree.toss_material_production_category_id}
                editable={editable}
                setValue={setValue}
                getValues={getValues}
                toggleOpen={() => {
                  setOpenGroupIds((preList) => {
                    if (x.toss_material_group_id == null) {
                      return preList;
                    }
                    if (preList.includes(x.toss_material_group_id)) {
                      return preList.filter((y) => y !== x.toss_material_group_id);
                    }
                    return [...preList, x.toss_material_group_id];
                  });
                }}
                control={control}
              />
            );
          })}
        </form>
      </div>
    );
  }
);

export default FListTab;
